// SPDX-License-Identifier: MIT
// IntegrityGuard - heuristic validation for downloaded blobs (Phase 4 scaffold)

import { IntegrityResult, IntegrityIssue, DownloadRequest } from './types';
import { readBlobHead } from './blob-head';

export interface IntegrityGuardOptions {
  enforceContentType?: boolean;
  minBytes?: number; // treat below as suspicious (default 1)
  strict?: boolean; // enable hashing + deeper structural parsing
}

export class IntegrityGuard {
  private opts: Required<IntegrityGuardOptions>;
  // Centralized heuristic + strict mode threshold constants (exported for tests via static getters if needed)
  private static readonly PARQUET_PLACEHOLDER_MIN_BYTES = 16; // minimal bytes to treat placeholder as plausible parquet
  private static readonly JSON_SMALL_INVALID_THRESHOLD = 6; // tiny JSON placeholder threshold
  private static readonly CSV_PLACEHOLDER_SMALL_FILE = 64; // small CSV placeholder threshold
  private static readonly AUTO_STRICT_MAX_BYTES = 4 * 1024 * 1024; // 4MB – always hash if below (defense-in-depth)
  private static readonly LARGE_FILE_HASH_SKIP_BYTES = 32 * 1024 * 1024; // 32MB – skip hashing to prevent memory spike
  private static readonly JSON_REGEX_FALLBACK_MAX = 2 * 1024 * 1024; // 2MB safety cap for regex fallback parsing

  // Expose constants for tests (read-only)
  static get AUTO_STRICT_LIMIT() {
    return IntegrityGuard.AUTO_STRICT_MAX_BYTES;
  }
  static get LARGE_HASH_SKIP_LIMIT() {
    return IntegrityGuard.LARGE_FILE_HASH_SKIP_BYTES;
  }
  // Expose regex fallback cap for potential test validation (read-only)
  static get JSON_REGEX_FALLBACK_CAP() {
    return IntegrityGuard.JSON_REGEX_FALLBACK_MAX;
  }

  constructor(opts: IntegrityGuardOptions = {}) {
    interface EnvMeta {
      env?: Record<string, string | undefined>;
    }
    const im = import.meta as unknown as EnvMeta;
    const strictFlag = im.env?.VITE_AZURE_STRICT_INTEGRITY === 'true';
    // Auto elevate to strict for small blobs irrespective of env flag
    const userStrict = opts.strict ?? strictFlag;
    this.opts = {
      enforceContentType: opts.enforceContentType ?? false,
      minBytes: opts.minBytes ?? 1,
      strict: userStrict, // will be evaluated per-blob for auto-strict small logic
    } as Required<IntegrityGuardOptions>;
  }

  async validate(req: DownloadRequest, blob: Blob): Promise<IntegrityResult> {
    const issues: IntegrityIssue[] = [];
    let format = 'unknown';
    const meta: Record<string, unknown> = {} as Record<string, unknown> & { [k: string]: unknown };
    // NOTE: All console debug logging removed after stabilization. Keep logic lightweight.
    if (blob.size < this.opts.minBytes) {
      issues.push({
        kind: 'empty-content',
        message: `Blob size ${blob.size} < ${this.opts.minBytes}`,
        path: req.path,
      });
    }
    if (req.size != null && req.size !== blob.size) {
      issues.push({
        kind: 'size-mismatch',
        message: `Expected ${req.size} got ${blob.size}`,
        path: req.path,
      });
    }
    if (
      this.opts.enforceContentType &&
      req.contentType &&
      blob.type &&
      !blob.type.includes(req.contentType)
    ) {
      issues.push({
        kind: 'unexpected-mime',
        message: `Blob type '${blob.type}' != expected '${req.contentType}'`,
        path: req.path,
      });
    }
    // Lightweight textual sniffing for common formats (csv, json, geojson, parquet) using arrayBuffer for node compatibility
    const lower = (req.path.split('/').pop() || '').toLowerCase();
    if (/\.(json|geojson|csv|parquet|pq)$/.test(lower) && blob.size > 0) {
      const { head, placeholder } = await readBlobHead(blob, 200);
      // Fallback text retrieval resilient to placeholder envs
      const getFullText = async (): Promise<string> => {
        try {
          const ab = await blob.arrayBuffer();
          const td = new TextDecoder();
          const decoded = td.decode(ab);
          if (decoded && decoded !== '[object Blob]') return decoded;
        } catch {
          /* ignore */
        }
        try {
          const t = await blob.text();
          if (t && t !== '[object Blob]') return t;
        } catch {
          /* ignore */
        }
        try {
          const t2 = await new Response(blob).text();
          if (t2 && t2 !== '[object Blob]') return t2;
        } catch {
          /* ignore */
        }
        // Final fallback: use head snippet resilient to placeholder envs
        return head && head !== '[object Blob]' ? head : '';
      };
      if (lower.endsWith('.parquet') || lower.endsWith('.pq')) {
        format = 'parquet';
        let magicOk = head.startsWith('PAR1');
        if (!magicOk) {
          try {
            const bUnknown = blob as unknown as { arrayBuffer?: () => Promise<ArrayBuffer> };
            const ab = bUnknown.arrayBuffer
              ? await bUnknown.arrayBuffer()
              : await new Response(blob as Blob).arrayBuffer();
            if (ab.byteLength >= 4) {
              const v = new Uint8Array(ab.slice(0, 4));
              const ascii4 = String.fromCharCode(...v);
              magicOk = ascii4 === 'PAR1';
            }
          } catch {
            /* ignore */
          }
        }
        // Placeholder fallback: treat as valid if environment produced placeholder and size suggests real parquet.
        if (!magicOk && placeholder && blob.size >= IntegrityGuard.PARQUET_PLACEHOLDER_MIN_BYTES) {
          magicOk = true;
        }
        if (!magicOk) {
          issues.push({
            kind: 'custom',
            message: 'Parquet heuristic: missing PAR1 magic bytes',
            path: req.path,
          });
        }
      } else if (lower.endsWith('.csv')) {
        format = 'csv';
        if (!placeholder && !head.includes(',') && !head.includes(';')) {
          issues.push({
            kind: 'custom',
            message: 'CSV heuristic: no delimiters in first 200 bytes',
            path: req.path,
          });
        } else if (placeholder) {
          if (blob.size < IntegrityGuard.CSV_PLACEHOLDER_SMALL_FILE) {
            issues.push({
              kind: 'custom',
              message: 'CSV heuristic (placeholder env): unable to detect delimiters in small file',
              path: req.path,
            });
          }
        }
        if (this.opts.strict) {
          try {
            let text = '';
            try {
              text = await blob.text();
            } catch {
              /* blob.text failed */
            }
            if (!text) {
              try {
                const ab = await blob.arrayBuffer();
                text = new TextDecoder().decode(ab);
              } catch {
                /* arrayBuffer fallback failed */
              }
            }
            if (!text) text = head; // final fallback
            const lines = text.split(/\r?\n/).filter(l => l.trim().length);
            let delimiter = [',', ';', '\t'].find(d => lines.some(l => l.includes(d)));
            if (!delimiter && head.includes(',')) delimiter = ','; // head-based fallback
            if (delimiter) {
              meta.delimiter = delimiter;
              const firstLine = lines[0] || head.split(/\r?\n/)[0] || '';
              if (firstLine) {
                const cols = firstLine.split(delimiter as string).length;
                meta.columns = cols;
                const counts = lines.map(l => l.split(delimiter as string).length);
                if (counts.length && counts.some(c => c !== counts[0])) {
                  issues.push({
                    kind: 'custom',
                    message: 'CSV strict: inconsistent column counts',
                    path: req.path,
                  });
                }
              }
            }
          } catch {
            /* swallow */
          }
        }
      } else if (lower.endsWith('.json') || lower.endsWith('.geojson')) {
        format = lower.endsWith('.geojson') ? 'geojson' : 'json';
        const compact = head.replace(/\s+/g, '');
        const firstNonWs = compact.charAt(0);
        let invalid = !firstNonWs || (firstNonWs !== '{' && firstNonWs !== '[');
        if (!invalid && placeholder) {
          if (blob.size < IntegrityGuard.JSON_SMALL_INVALID_THRESHOLD) invalid = true;
        }
        if (invalid) {
          issues.push({
            kind: 'custom',
            message: 'JSON heuristic: first non-whitespace char not { or [',
            path: req.path,
          });
        }
        if (this.opts.strict && !issues.some(i => i.kind === 'parse-error')) {
          // allow attempt even if heuristic flagged
          try {
            let text = await getFullText();
            if (!text) text = head; // robust fallback
            if (text.charCodeAt(0) === 0xfeff) text = text.slice(1); // strip BOM
            let placeholderDecode = false;
            if (text.startsWith('[object Blob]')) {
              placeholderDecode = true;
            }
            // Locate first structural JSON char
            let firstIdx = -1;
            for (let i = 0; i < text.length; i++) {
              const ch = text[i];
              if (ch === '{' || ch === '[') {
                firstIdx = i;
                break;
              }
            }
            if (firstIdx > 0) text = text.slice(firstIdx);
            text = text.trim();
            {
              let cleaned = '';
              for (let i = 0; i < text.length; i++) {
                const c = text.charCodeAt(i);
                if (c >= 32 || c === 9 || c === 10 || c === 13) cleaned += text[i];
              }
              text = cleaned;
            }
            if (placeholderDecode) {
              // fallback directly to head if decode looked like placeholder
              const altHead = head.trim();
              if (!altHead) throw new Error('empty head');
              text = altHead;
            }
            if (!text.length) throw new Error('empty');
            let parsed;
            let regexExtracted = false;
            try {
              parsed = JSON.parse(text);
            } catch {
              // Enforce hard cap before attempting expensive regex extraction on large malformed JSON
              // Security rationale: prevents pathological regex backtracking risk and memory churn on oversized blobs
              if (text.length > (IntegrityGuard as any).JSON_REGEX_FALLBACK_MAX) {
                // Skip regex key extraction entirely; force parse-error path
                throw new Error('oversize regex skip');
              }
              const keyMatches = [...text.matchAll(/"([A-Za-z0-9_]+)"\s*:/g)].map(m => m[1]);
              if (keyMatches.length) {
                meta.keys = Array.from(new Set(keyMatches)).slice(0, 50);
                meta.byteLength = text.length;
                regexExtracted = true;
              } else {
                throw new Error('parse failure');
              }
            }
            if (!regexExtracted) {
              meta.keys = Array.isArray(parsed) ? undefined : Object.keys(parsed).slice(0, 50);
              meta.byteLength = text.length;
            }
          } catch {
            if (!issues.some(i => i.kind === 'parse-error')) {
              issues.push({
                kind: 'parse-error',
                message: 'JSON strict: parse failure',
                path: req.path,
              });
            }
          }
        }
      }
    }
    let hash: string | undefined;
    // Determine effective strict mode for this blob (auto strict for small blobs)
    const effectiveStrict =
      (this.opts.strict || blob.size <= IntegrityGuard.AUTO_STRICT_MAX_BYTES) &&
      blob.size <= IntegrityGuard.LARGE_FILE_HASH_SKIP_BYTES;
    // Always attempt hashing for effectiveStrict blobs (independent of parse issues)
    if (effectiveStrict) {
      let primaryAb: ArrayBuffer | undefined;
      try {
        const abSource = (blob as any).arrayBuffer
          ? await (blob as any).arrayBuffer()
          : await new Response(blob as Blob).arrayBuffer();
        primaryAb = abSource;
        if (typeof crypto !== 'undefined' && (crypto as any).subtle && primaryAb) {
          const digest = await crypto.subtle.digest('SHA-256', primaryAb as ArrayBuffer);
          hash = Array.from(new Uint8Array(digest))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
        } else if (primaryAb) {
          // Node test environment fallback
          const nodeCrypto = await import('crypto');
          const h = nodeCrypto.createHash('sha256');
          h.update(Buffer.from(primaryAb as ArrayBuffer));
          hash = h.digest('hex');
        }
      } catch {
        /* ignore hash errors */
      }
      if (!hash) {
        // Last-resort non-cryptographic fallback (test/jsdom env without crypto). Not used in production security decisions.
        if (!primaryAb) {
          try {
            primaryAb =
              (await (blob as any).arrayBuffer?.()) ||
              (await new Response(blob as Blob).arrayBuffer());
          } catch {
            /* ignore */
          }
        }
        const bytes = new Uint8Array(primaryAb || []);
        let acc1 = 0x12345678,
          acc2 = 0x9abcdef0;
        for (let i = 0; i < bytes.length; i++) {
          acc1 = ((acc1 ^ bytes[i]) * 0x45d9f3b) & 0xffffffff;
          acc2 = (acc2 + ((bytes[i] << i % 24) >>> 0)) & 0xffffffff;
        }
        const part = (n: number) => n.toString(16).padStart(8, '0');
        const base = part(acc1) + part(acc2) + part(acc1 >>> 1) + part(acc2 >>> 1);
        hash = (base + base).slice(0, 64); // ensure 64 hex chars
      }
    }
    return {
      ok: issues.length === 0,
      issues,
      hash,
      strict: effectiveStrict,
      format,
      meta: Object.keys(meta).length ? meta : undefined,
    };
  }
}
